/*
  GAROE Moments, product pages. Native Shopify only (living document,
  product-options decision): the variant carries the price, and every
  customer-entered detail travels as a line item property.

  product-form     Adds to the basket over the Cart API, then opens the drawer.
  tier-selector    My First Match Together. Standard or Personalised, live
                   price, and the personalisation panel opens only for
                   Personalised.
  unit-form        Marathon Plaque. Units first, then everything else. The
                   unit is fixed onto the distance and each split, and
                   changing unit clears them rather than relabelling them.
  product-gallery  Thumbnail switching.
  sticky-atc       Mobile add-to-basket bar.
*/
(() => {
  if (customElements.get('product-form')) return;

  const config = window.GAROE || { routes: {}, strings: {} };
  const strings = config.strings || {};

  const PROPERTY_NAME = /^properties\[(.+)\]$/;

  const dateFormatter = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

  /* "2026-04-26" becomes "26 April 2026", so the order reads the way it prints. */
  function formatDate(iso) {
    const [year, month, day] = iso.split('-').map(Number);
    if (!year || !month || !day) return iso;
    return dateFormatter.format(new Date(Date.UTC(year, month - 1, day)));
  }

  /* ---------- Add to basket ---------- */

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.querySelector('[data-add-button]');
      this.error = this.querySelector('[data-form-error]');
      if (this.form) this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message || '';
      this.error.hidden = !message;
    }

    collectItem() {
      const data = new FormData(this.form);
      const properties = {};
      for (const [key, value] of data.entries()) {
        const match = key.match(PROPERTY_NAME);
        if (!match) continue;
        const text = String(value).trim();
        if (text) properties[match[1]] = text;
      }
      this.form.querySelectorAll('input[type="date"][data-format="date"]').forEach((input) => {
        const match = input.name.match(PROPERTY_NAME);
        if (match && properties[match[1]]) properties[match[1]] = formatDate(properties[match[1]]);
      });
      return {
        id: Number(data.get('id')),
        quantity: Number(data.get('quantity') || 1),
        properties,
      };
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.busy) return;
      this.showError('');

      // Product-specific components set their custom validity messages here.
      this.dispatchEvent(new CustomEvent('product-form:validate'));
      if (!this.form.reportValidity()) return;

      const item = this.collectItem();
      this.dispatchEvent(new CustomEvent('product-form:serialize', { detail: { properties: item.properties } }));

      this.setBusy(true);
      try {
        const response = await fetch(`${config.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [item],
            sections: 'cart-drawer',
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();
        if (!response.ok) {
          this.showError(data.description || data.message || strings.errorGeneric);
          return;
        }
        const drawer = document.querySelector('cart-drawer');
        if (drawer && data.sections && data.sections['cart-drawer']) {
          drawer.renderSection(data.sections['cart-drawer']);
          drawer.open(this.button);
        } else {
          window.location.href = config.routes.cart;
        }
      } catch (error) {
        this.showError(strings.errorGeneric);
      } finally {
        this.setBusy(false);
      }
    }

    setBusy(busy) {
      this.busy = busy;
      if (!this.button) return;
      this.button.disabled = busy;
      this.button.setAttribute('aria-busy', String(busy));
      const label = this.button.querySelector('[data-add-label]');
      if (!label) return;
      if (busy) {
        this.idleLabel = label.textContent;
        label.textContent = strings.adding || label.textContent;
      } else if (this.idleLabel) {
        label.textContent = this.idleLabel;
      }
    }
  }

  /* ---------- My First Match Together: Standard or Personalised ---------- */

  class TierSelector extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('[data-product-section]');
      const form = this.closest('form');
      this.panel = form ? form.querySelector('[data-reveal-panel]') : null;
      this.addEventListener('change', () => this.update(false));
      this.update(true);
    }

    update(initial) {
      const input = this.querySelector('input[name="id"]:checked');
      if (!input) return;
      const reveals = input.dataset.reveals === 'true';

      if (this.panel) {
        // A closed panel is disabled too, so its fields never reach a Standard order.
        this.panel.toggleAttribute('data-collapsed', !reveals);
        this.panel.disabled = !reveals;
        this.panel.querySelectorAll('[data-required]').forEach((field) => {
          field.required = reveals;
        });
      }

      if (this.section) {
        this.section.querySelectorAll('[data-live-price]').forEach((el) => {
          el.textContent = input.dataset.price;
        });
      }

      if (!initial) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', input.value);
        window.history.replaceState({}, '', url.toString());
      }
    }
  }

  /* ---------- Marathon Plaque: units, distance and splits ---------- */

  class UnitForm extends HTMLElement {
    connectedCallback() {
      this.productForm = this.closest('product-form');
      this.after = this.querySelector('[data-after-unit]');
      this.notice = this.querySelector('[data-unit-notice]');
      this.list = this.querySelector('[data-split-list]');
      this.template = this.querySelector('[data-split-template]');
      this.addButton = this.querySelector('[data-split-add]');
      this.limitNote = this.querySelector('[data-split-limit]');
      this.distance = this.querySelector('[data-distance-input]');
      this.maxSplits = Number(this.dataset.maxSplits || 0);
      this.splitPrefix = this.dataset.splitProperty || 'Split';
      this.unitInputs = [...this.querySelectorAll('input[data-unit-abbr]')];
      this.unit = this.currentUnit();

      this.unitInputs.forEach((input) => input.addEventListener('change', () => this.onUnitChange()));
      if (this.addButton) this.addButton.addEventListener('click', () => this.addRow());
      if (this.list) {
        this.list.addEventListener('click', (event) => {
          const remove = event.target.closest('[data-split-remove]');
          if (remove) this.removeRow(remove.closest('li'));
        });
      }
      // Split errors depend on the whole row and on the race distance, so any
      // edit clears them all. validate() recomputes them on the next submit.
      this.addEventListener('input', () => this.clearSplitErrors());
      if (this.productForm) {
        this.productForm.addEventListener('product-form:validate', () => this.validate());
        this.productForm.addEventListener('product-form:serialize', (event) => this.serialize(event.detail.properties));
      }

      this.applyUnit();
    }

    currentUnit() {
      return this.unitInputs.find((input) => input.checked) || null;
    }

    rows() {
      return this.list ? [...this.list.children] : [];
    }

    /* Nothing below the unit question is shown, or submitted, until a unit is chosen. */
    applyUnit() {
      const unit = this.currentUnit();
      if (this.after) {
        this.after.toggleAttribute('data-collapsed', !unit);
        this.after.disabled = !unit;
      }
      const abbr = unit ? unit.dataset.unitAbbr : '';
      this.querySelectorAll('[data-unit-suffix]').forEach((el) => {
        el.textContent = abbr;
      });
      this.updateAddState();
    }

    onUnitChange() {
      const next = this.currentUnit();
      const previous = this.unit;
      if (previous && next && previous !== next && this.hasEnteredValues()) {
        this.clearValues();
        if (this.notice) {
          this.notice.textContent = (strings.unitsCleared || '').replace('[unit]', previous.value.toLowerCase());
          this.notice.hidden = false;
        }
      } else if (this.notice) {
        this.notice.hidden = true;
      }
      this.unit = next;
      this.applyUnit();
    }

    hasEnteredValues() {
      if (this.distance && this.distance.value) return true;
      return this.rows().some((row) => [...row.querySelectorAll('input')].some((input) => input.value.trim()));
    }

    clearValues() {
      if (this.distance) this.distance.value = '';
      if (this.list) this.list.innerHTML = '';
    }

    addRow() {
      if (!this.template || !this.list || this.rows().length >= this.maxSplits) return;
      const row = this.template.content.firstElementChild.cloneNode(true);
      const unit = this.currentUnit();
      row.querySelectorAll('[data-unit-suffix]').forEach((el) => {
        el.textContent = unit ? unit.dataset.unitAbbr : '';
      });
      this.list.appendChild(row);
      this.renumber();
      this.updateAddState();
      const first = row.querySelector('[data-split-distance]');
      if (first) first.focus();
    }

    removeRow(row) {
      if (!row) return;
      row.remove();
      this.renumber();
      this.updateAddState();
      if (this.addButton && !this.addButton.hidden) this.addButton.focus();
    }

    renumber() {
      this.rows().forEach((row, index) => {
        const label = row.querySelector('[data-split-index]');
        if (label) label.textContent = String(index + 1);
      });
    }

    updateAddState() {
      if (!this.addButton) return;
      const full = this.rows().length >= this.maxSplits;
      this.addButton.hidden = full;
      if (this.limitNote) this.limitNote.hidden = !full;
    }

    clearSplitErrors() {
      this.rows().forEach((row) => {
        row.querySelectorAll('input').forEach((input) => input.setCustomValidity(''));
      });
    }

    validate() {
      const total = this.distance && this.distance.value ? Number(this.distance.value) : null;
      this.clearSplitErrors();
      this.rows().forEach((row) => {
        const distance = row.querySelector('[data-split-distance]');
        const time = row.querySelector('[data-split-time]');
        const hasDistance = distance.value.trim() !== '';
        const hasTime = time.value.trim() !== '';
        if (hasDistance !== hasTime) {
          (hasDistance ? time : distance).setCustomValidity(strings.splitIncomplete || '');
        } else if (hasDistance && total !== null && Number(distance.value) > total) {
          distance.setCustomValidity(strings.splitTooFar || '');
        }
      });
    }

    /* The unit goes onto the values themselves, e.g. "42.195 km" and "Split 1: 5 km: 24:10". */
    serialize(properties) {
      const unit = this.currentUnit();
      if (!unit) return;
      const abbr = unit.dataset.unitAbbr;

      if (this.distance && this.distance.value) {
        const match = this.distance.name.match(PROPERTY_NAME);
        if (match) properties[match[1]] = `${this.distance.value} ${abbr}`;
      }

      let count = 0;
      this.rows().forEach((row) => {
        const distance = row.querySelector('[data-split-distance]').value.trim();
        const time = row.querySelector('[data-split-time]').value.trim();
        if (!distance || !time) return;
        count += 1;
        properties[`${this.splitPrefix} ${count}`] = `${distance} ${abbr}: ${time}`;
      });
    }
  }

  /* ---------- Gallery thumbnails ---------- */

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-gallery-thumb]');
        if (!thumb) return;
        const index = Number(thumb.dataset.galleryThumb);
        this.querySelectorAll('[data-gallery-slide]').forEach((slide, i) => {
          slide.hidden = i !== index;
        });
        this.querySelectorAll('[data-gallery-thumb]').forEach((button) => {
          button.setAttribute('aria-current', String(button === thumb));
        });
      });
    }
  }

  /* ---------- Mobile sticky add-to-basket bar ---------- */

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const form = document.getElementById(this.dataset.watch);
      const target = form ? form.querySelector('[data-add-button]') : null;
      if (!target || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(([entry]) => {
        const visible = !entry.isIntersecting;
        this.classList.toggle('is-visible', visible);
        this.inert = !visible;
      });
      this.observer.observe(target);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }

  customElements.define('product-form', ProductForm);
  customElements.define('tier-selector', TierSelector);
  customElements.define('unit-form', UnitForm);
  customElements.define('product-gallery', ProductGallery);
  customElements.define('sticky-atc', StickyAtc);
})();
